import * as React from 'react';
import { cn } from '@/lib/utils';
import {
  type AdminNotificationStackItem,
  issueCountLabel,
  sortAdminNotificationStackItems,
} from '@/lib/admin-notification-stack.types';
import {
  type AdminNotificationStackProps,
  SeverityIcon,
  notificationBarSeverity,
  severityStyles,
} from '@/components/admin/admin-notification-stack-shared';

export type { AdminNotificationStackProps };

const AdminNotificationStackMotion = React.lazy(() =>
  import('@/components/admin/admin-notification-stack-motion').then((module) => ({
    default: module.AdminNotificationStackMotion,
  })),
);

type StaticBarProps = {
  count: number;
  barSeverity: ReturnType<typeof notificationBarSeverity>;
  hasCritical: boolean;
  className?: string;
  onActivateMotion: () => void;
};

function AdminNotificationStaticBar({
  count,
  barSeverity,
  hasCritical,
  className,
  onActivateMotion,
}: StaticBarProps) {
  const barStyle = severityStyles[barSeverity];

  return (
    <div className={cn('relative h-8 w-auto shrink-0', className)}>
      <button
        type="button"
        title={issueCountLabel(count)}
        className={cn(
          'relative inline-flex h-8 min-w-8 items-center gap-1 rounded-full border px-2 text-left shadow-sm transition-shadow hover:shadow-md',
          barStyle.bar,
          hasCritical && 'animate-[admin-notif-pulse_2s_ease-in-out_infinite]',
        )}
        onClick={onActivateMotion}
        aria-haspopup="dialog"
        aria-label={`${issueCountLabel(count)}. Expand notification stack.`}
      >
        <SeverityIcon severity={barSeverity} className="h-3.5 w-3.5" />
        <span className="text-xs font-semibold tabular-nums leading-none">{count}</span>
        <span className="sr-only">{issueCountLabel(count)}</span>
      </button>
      <style>{`
        @keyframes admin-notif-pulse {
          0%, 100% { box-shadow: 0 0 0 0 transparent; }
          50% { box-shadow: 0 0 0 3px hsl(var(--destructive) / 0.25); }
        }
      `}</style>
    </div>
  );
}

export function AdminNotificationStack(props: AdminNotificationStackProps) {
  const active = React.useMemo(
    () => sortAdminNotificationStackItems(props.notifications),
    [props.notifications],
  );
  const count = active.length;
  const barSeverity = notificationBarSeverity(active);
  const hasCritical = active.some((n) => n.severity === 'critical');
  const [useMotion, setUseMotion] = React.useState(false);

  React.useEffect(() => {
    if (count === 0) setUseMotion(false);
  }, [count]);

  if (count === 0) return null;

  if (useMotion) {
    return (
      <React.Suspense
        fallback={
          <AdminNotificationStaticBar
            count={count}
            barSeverity={barSeverity}
            hasCritical={hasCritical}
            className={props.className}
            onActivateMotion={() => setUseMotion(true)}
          />
        }
      >
        <AdminNotificationStackMotion {...props} initiallyExpanded />
      </React.Suspense>
    );
  }

  return (
    <AdminNotificationStaticBar
      count={count}
      barSeverity={barSeverity}
      hasCritical={hasCritical}
      className={props.className}
      onActivateMotion={() => setUseMotion(true)}
    />
  );
}
